import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Link2, Loader2, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Btn, Card, EmptyState, PageHeader, Pagination } from "@/components/dashboard/Ui";

export default function IntakeLinksPage() {
  const qc = useQueryClient();
  const [phones, setPhones] = useState("");
  const [label, setLabel] = useState("");
  const [page, setPage] = useState(1);
  const [created, setCreated] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["intake-links", page], queryFn: async () => (await api.get("/intake-links", { params: { page, page_size: 50 } })).data });

  const create = useMutation({
    mutationFn: async () => {
      const list = phones
        .split(/[\n,;]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      return (await api.post("/intake-links", { phones: list, label: label || null })).data;
    },
    onSuccess: (res) => {
      setCreated(res);
      setPhones("");
      if (res.created.length) toast.success(`${res.created.length} link${res.created.length > 1 ? "s" : ""} generated`);
      if (res.invalid.length) toast.warning(`${res.invalid.length} number${res.invalid.length > 1 ? "s were" : " was"} invalid and skipped`);
      qc.invalidateQueries({ queryKey: ["intake-links"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/intake-links/${id}`)).data,
    onSuccess: () => {
      toast.success("Link deleted");
      qc.invalidateQueries({ queryKey: ["intake-links"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy");
    }
  };
  const copyAll = () => created && copy(created.created.map((c) => `${c.phone}\t${c.url}`).join("\n"));
  const genericLink = data?.base_url || `${window.location.origin}/intake`;

  return (
    <div data-testid="intake-links-page">
      <PageHeader title="Intake links" description="Generate trackable intake links to paste into your WhatsApp automation. Each link ties the submission back to the number you sent it to." />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
        <div className="space-y-6">
          <Card className="p-5">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Link2 className="h-5 w-5 text-primary" /> Generic link
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Works for anyone. Use it on social media or your website - the client types their own phone number.</p>
            <div className="mt-3 flex gap-2">
              <input readOnly className="emerge-input !h-10 flex-1 text-sm" value={genericLink} data-testid="intake-link-input" dir="ltr" />
              <Btn variant="secondary" onClick={() => copy(genericLink)} data-testid="intake-link-copy-button">
                <Copy /> Copy
              </Btn>
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <MessageCircle className="h-5 w-5 text-[hsl(var(--app-leaf))]" /> Tracked links for WhatsApp
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Paste one phone number per line (Lebanon +961 assumed if no country code). One unique link is created per number.</p>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate();
              }}
            >
              <textarea rows={6} className="emerge-input !h-auto py-2 font-mono text-sm" placeholder={"+961 71 234 567\n03 445 566\n+33 6 12 34 56 78"} value={phones} onChange={(e) => setPhones(e.target.value)} dir="ltr" data-testid="intake-links-phones-textarea" />
              <input className="emerge-input !h-10 text-sm" placeholder="Batch label (optional), e.g. School outreach March" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} data-testid="intake-links-label-input" />
              <Btn type="submit" disabled={!phones.trim() || create.isPending} className="w-full" data-testid="intake-links-generate-button">
                {create.isPending ? <Loader2 className="animate-spin" /> : <Link2 />} Generate links
              </Btn>
            </form>
            {created && created.created.length > 0 && (
              <div className="mt-4 rounded-[10px] border border-[hsl(var(--app-leaf))]/30 bg-[hsl(var(--app-leaf))]/6 p-3" data-testid="intake-links-created">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <Check className="h-4 w-4 text-[hsl(var(--app-leaf))]" /> {created.created.length} link{created.created.length > 1 ? "s" : ""} ready
                  </p>
                  <Btn variant="secondary" size="sm" onClick={copyAll} data-testid="intake-links-copy-all-button">
                    <Copy /> Copy all (phone + link)
                  </Btn>
                </div>
                <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
                  {created.created.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1.5" dir="ltr">
                      <span className="font-medium">{c.phone}</span>
                      <button type="button" onClick={() => copy(c.url)} className="truncate font-mono text-primary hover:underline" title="Copy link">
                        {c.url}
                      </button>
                    </li>
                  ))}
                </ul>
                {created.invalid.length > 0 && <p className="mt-2 text-xs text-destructive">Skipped invalid: {created.invalid.join(", ")}</p>}
              </div>
            )}
          </Card>
        </div>

        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="text-lg font-semibold">Send log</h2>
            <span className="text-sm text-muted-foreground">{data?.total ?? 0} links</span>
          </div>
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (data?.items || []).length === 0 ? (
            <EmptyState icon={Link2} title="No tracked links yet" description="Generate links on the left and they will be listed here with their usage status." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm" data-testid="intake-links-table">
                <thead className="bg-secondary/70 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-start font-semibold">Phone</th>
                    <th className="px-4 py-3 text-start font-semibold">Label</th>
                    <th className="px-4 py-3 text-start font-semibold">Created</th>
                    <th className="px-4 py-3 text-start font-semibold">Status</th>
                    <th className="px-4 py-3 text-end font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((l) => (
                    <tr key={l.id} className="border-t border-border table-row-hover" data-testid="intake-links-row">
                      <td className="px-4 py-3 font-medium" dir="ltr">
                        {l.phone}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{l.label || "-"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {fmtDateTime(l.created_at)}
                        <p className="text-xs">by {l.created_by_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        {l.used_at ? (
                          <a href={`/dashboard/submissions/${l.submission_id}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(140_25%_80%)] bg-[hsl(140_35%_92%)] px-2.5 py-0.5 text-xs font-semibold text-[hsl(140_43%_28%)] hover:underline">
                            <Check className="h-3 w-3" /> Submitted {fmtDateTime(l.used_at)}
                          </a>
                        ) : (
                          <span className="rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">Not used yet</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Btn variant="ghost" size="icon" title="Copy link" onClick={() => copy(l.url)} data-testid="intake-link-row-copy-button">
                            <Copy />
                          </Btn>
                          {!l.used_at && (
                            <Btn variant="ghost" size="icon" title="Delete" onClick={() => remove.mutate(l.id)} data-testid="intake-link-row-delete-button">
                              <Trash2 className="text-destructive" />
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && <Pagination page={page} pageSize={50} total={data.total} onChange={setPage} />}
        </Card>
      </div>
    </div>
  );
}
